import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Smartphone, Plus, ToggleLeft, ToggleRight, ArrowRight, Banknote, Trash2, Edit2
} from 'lucide-react';
import { OwnerLayout } from '../../components/OwnerLayout';
import api from '../../api';

const SERVICE_ICONS = {
  sound_box: '🔊', upi_qr: '📱', current_savings: '🏦',
  daily_account: '📅', fixed_deposit: '📈', recurring_deposit: '🔁',
  monthly_interest: '💵', loan: '💰', other: '📦'
};

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`} style={{ bottom: '24px' }}>{msg}</div>;
}

function fmtINR(n) {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  return `\u20B9${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

const EMPTY_SCHEME = { name: '', glCode: '', description: '', interestRate: '', tenureMonths: '', minAmount: '', maxAmount: '' };

export default function OwnerCatalog() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('services');
  const [toast, setToast] = useState({ show: false, msg: '' });

  // Services
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [showAddService, setShowAddService] = useState(false);
  const [newService, setNewService] = useState({ name: '', description: '' });

  // Loan schemes
  const [schemes, setSchemes] = useState([]);
  const [loadingSchemes, setLoadingSchemes] = useState(true);
  const [showSchemeModal, setShowSchemeModal] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [schemeForm, setSchemeForm] = useState(EMPTY_SCHEME);

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const loadServices = useCallback(async () => {
    try {
      const res = await api.get('/services/all');
      setServices(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoadingServices(false); }
  }, []);

  const loadSchemes = useCallback(async () => {
    try {
      const res = await api.get('/loan-schemes');
      setSchemes(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoadingSchemes(false); }
  }, []);

  useEffect(() => { loadServices(); loadSchemes(); }, [loadServices, loadSchemes]);

  // Services handlers
  const handleToggleService = async (id, e) => {
    e.stopPropagation();
    try {
      await api.patch(`/services/${id}/toggle`);
      setServices((prev) => prev.map((s) => (s.id === id ? { ...s, isActive: !s.isActive } : s)));
      showToast('Service toggled');
    } catch { showToast('Failed to toggle'); }
  };

  const handleAddService = async () => {
    if (!newService.name.trim()) return;
    try {
      const res = await api.post('/services', newService);
      setServices((prev) => [...prev, res.data]);
      setNewService({ name: '', description: '' });
      setShowAddService(false);
      showToast('Service added');
    } catch (err) { showToast(err.response?.data?.detail || 'Failed to add'); }
  };

  // Loan scheme handlers
  const openCreateScheme = () => {
    setEditingScheme(null);
    setSchemeForm(EMPTY_SCHEME);
    setShowSchemeModal(true);
  };

  const openEditScheme = (s) => {
    setEditingScheme(s);
    setSchemeForm({
      name: s.name || '',
      glCode: s.glCode ?? '',
      description: s.description || '',
      interestRate: s.interestRate ?? '',
      tenureMonths: s.tenureMonths ?? '',
      minAmount: s.minAmount ?? '',
      maxAmount: s.maxAmount ?? '',
    });
    setShowSchemeModal(true);
  };

  const saveScheme = async () => {
    if (!schemeForm.name.trim()) {
      showToast('Scheme name is required');
      return;
    }
    const payload = {
      name: schemeForm.name.trim(),
      glCode: schemeForm.glCode === '' ? null : parseInt(schemeForm.glCode, 10),
      description: schemeForm.description.trim(),
      interestRate: schemeForm.interestRate === '' ? null : parseFloat(schemeForm.interestRate),
      tenureMonths: schemeForm.tenureMonths === '' ? null : parseInt(schemeForm.tenureMonths, 10),
      minAmount: schemeForm.minAmount === '' ? null : parseFloat(schemeForm.minAmount),
      maxAmount: schemeForm.maxAmount === '' ? null : parseFloat(schemeForm.maxAmount),
    };
    try {
      if (editingScheme) {
        await api.patch(`/loan-schemes/${editingScheme.id}`, payload);
        showToast('Scheme updated');
      } else {
        await api.post('/loan-schemes', payload);
        showToast('Scheme added');
      }
      setShowSchemeModal(false);
      loadSchemes();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Save failed');
    }
  };

  const toggleScheme = async (s) => {
    try {
      await api.patch(`/loan-schemes/${s.id}/toggle`);
      setSchemes((prev) => prev.map((x) => (x.id === s.id ? { ...x, isActive: !x.isActive } : x)));
      showToast('Scheme toggled');
    } catch { showToast('Failed to toggle'); }
  };

  const removeScheme = async (s) => {
    if (!window.confirm(`Delete loan scheme "${s.name}"?\n\nSchemes already used by loans cannot be deleted — deactivate them instead.`)) return;
    try {
      await api.delete(`/loan-schemes/${s.id}`);
      setSchemes((prev) => prev.filter((x) => x.id !== s.id));
      showToast('Scheme deleted');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Delete failed');
    }
  };

  const totalMerchants = services.reduce((sum, s) => sum + (s.merchantCount || 0), 0);
  const totalLoans = schemes.reduce((sum, s) => sum + (s.loanCount || 0), 0);

  return (
    <OwnerLayout title="Products & Schemes">
      {/* Tabs */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-navy text-lg">Products & Schemes</h2>
          <p className="text-sm text-gray">Manage services offered to merchants and loan schemes for recovery accounts</p>
        </div>
        <div>
          {tab === 'services' ? (
            <button className="btn-navy" onClick={() => setShowAddService(true)}>
              <Plus size={14} style={{ marginRight: '4px' }} /> Add Service
            </button>
          ) : (
            <button className="btn-navy" onClick={openCreateScheme}>
              <Plus size={14} style={{ marginRight: '4px' }} /> Add Loan Scheme
            </button>
          )}
        </div>
      </div>

      <div className="panel" style={{ padding: '0', marginBottom: '16px' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--g2)' }}>
          <TabBtn active={tab === 'services'} onClick={() => setTab('services')} icon={<Smartphone size={14} />} label="Services" count={services.length} sub={`${totalMerchants} merchants`} />
          <TabBtn active={tab === 'schemes'} onClick={() => setTab('schemes')} icon={<Banknote size={14} />} label="Loan Schemes" count={schemes.length} sub={`${totalLoans} loans`} />
        </div>
      </div>

      {tab === 'services' ? (
        <ServicesGrid services={services} loading={loadingServices} onToggle={handleToggleService} onClick={(id) => navigate(`/owner/merchants?service=${id}`)} />
      ) : (
        <SchemesGrid schemes={schemes} loading={loadingSchemes} onEdit={openEditScheme} onToggle={toggleScheme} onDelete={removeScheme} />
      )}

      {/* Add Service Modal */}
      {showAddService && (
        <div className="owner-modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) setShowAddService(false); }}>
          <div className="owner-modal" style={{ width: '420px' }}>
            <div className="modal-head">
              <div className="modal-icon"><Plus size={16} /></div>
              <div>
                <div className="modal-title">Add New Service</div>
                <div className="modal-subtitle">Create a new service offering</div>
              </div>
              <button className="modal-close" onClick={() => setShowAddService(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Service Name</label>
                <input className="form-input" placeholder="e.g. Gold Loan" value={newService.name} onChange={(e) => setNewService((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Description (optional)</label>
                <textarea className="form-input" rows={2} value={newService.description} onChange={(e) => setNewService((p) => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn-outline" onClick={() => setShowAddService(false)}>Cancel</button>
                <button className="btn-navy" onClick={handleAddService}>Add Service</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Scheme Modal */}
      {showSchemeModal && (
        <div className="owner-modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) setShowSchemeModal(false); }}>
          <div className="owner-modal" style={{ width: '480px' }}>
            <div className="modal-head">
              <div className="modal-icon"><Banknote size={16} /></div>
              <div>
                <div className="modal-title">{editingScheme ? 'Edit Loan Scheme' : 'Add Loan Scheme'}</div>
                <div className="modal-subtitle">Used when creating loan accounts in Recovery</div>
              </div>
              <button className="modal-close" onClick={() => setShowSchemeModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Scheme Name</label>
                <input className="form-input" placeholder="e.g. Personal Loan, Gold Loan" value={schemeForm.name} onChange={(e) => setSchemeForm((p) => ({ ...p, name: e.target.value }))} disabled={!!editingScheme} />
                {editingScheme && <div className="text-xs text-gray" style={{ marginTop: '4px' }}>Name cannot be changed once loans exist on this scheme</div>}
              </div>
              <div className="form-group">
                <label className="form-label">CBS GL Code</label>
                <input className="form-input" type="number" placeholder="e.g. 93 for Personal Loan" value={schemeForm.glCode} onChange={(e) => setSchemeForm((p) => ({ ...p, glCode: e.target.value }))} disabled={!!editingScheme && editingScheme.glCode != null} />
                <div className="text-xs text-gray" style={{ marginTop: '4px' }}>Maps to the G.L. Head printed on CBS overdue reports. Cannot be changed once set.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Description (optional)</label>
                <textarea className="form-input" rows={2} value={schemeForm.description} onChange={(e) => setSchemeForm((p) => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="form-group">
                  <label className="form-label">Interest Rate (% p.a.)</label>
                  <input className="form-input" type="number" step="0.01" placeholder="e.g. 14.5" value={schemeForm.interestRate} onChange={(e) => setSchemeForm((p) => ({ ...p, interestRate: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Tenure (months)</label>
                  <input className="form-input" type="number" placeholder="e.g. 24" value={schemeForm.tenureMonths} onChange={(e) => setSchemeForm((p) => ({ ...p, tenureMonths: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Min Amount</label>
                  <input className="form-input" type="number" placeholder="optional" value={schemeForm.minAmount} onChange={(e) => setSchemeForm((p) => ({ ...p, minAmount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Max Amount</label>
                  <input className="form-input" type="number" placeholder="optional" value={schemeForm.maxAmount} onChange={(e) => setSchemeForm((p) => ({ ...p, maxAmount: e.target.value }))} />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn-outline" onClick={() => setShowSchemeModal(false)}>Cancel</button>
                <button className="btn-navy" onClick={saveScheme}>{editingScheme ? 'Save Changes' : 'Add Scheme'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast.msg} show={toast.show} />
    </OwnerLayout>
  );
}

function TabBtn({ active, onClick, icon, label, count, sub }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '14px 18px',
        background: active ? 'var(--g1)' : 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--navy)' : '2px solid transparent',
        cursor: 'pointer',
        textAlign: 'left',
        color: active ? 'var(--navy)' : 'var(--g3)',
        fontFamily: 'var(--fh)',
        transition: 'all 0.12s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '14px' }}>
        {icon} {label} <span style={{ color: 'var(--g4)', fontWeight: 600 }}>({count})</span>
      </div>
      <div style={{ fontSize: '11px', color: 'var(--g3)', marginTop: '2px', fontWeight: 500 }}>{sub}</div>
    </button>
  );
}

function ServicesGrid({ services, loading, onToggle, onClick }) {
  if (loading) return <div className="panel" style={{ padding: '24px', textAlign: 'center', color: 'var(--g3)' }}>Loading…</div>;
  if (services.length === 0) return <div className="panel" style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>No services configured yet.</div>;
  return (
    <div className="services-grid">
      {services.map((svc) => {
        const isPrimary = svc.slug === 'upi_qr';
        const isAutoIncluded = svc.slug === 'current_savings';
        return (
          <div key={svc.id} className={`service-card clickable ${isPrimary ? 'upi-primary' : ''}`} onClick={() => onClick(svc.id)}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <span style={{ fontSize: '20px' }}>{SERVICE_ICONS[svc.slug] || '📋'}</span>
                <div>
                  <div className="font-heading font-bold text-navy">{svc.name}</div>
                  {isPrimary && <span className="text-xs text-red font-semibold">PRIMARY</span>}
                  {isAutoIncluded && <span className="text-xs text-green-600 font-semibold">AUTO-INCLUDED</span>}
                </div>
              </div>
              <button
                className={`p-1 ${svc.isActive ? 'text-green-600' : 'text-gray-400'}`}
                onClick={(e) => onToggle(svc.id, e)}
                title={svc.isActive ? 'Click to deactivate' : 'Click to activate'}
              >
                {svc.isActive ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
              </button>
            </div>
            <div className="text-sm text-gray mb-2">{svc.description || 'No description'}</div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <div className="text-xs text-gray">Merchants interested</div>
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold text-navy">{svc.merchantCount || 0}</span>
                <ArrowRight size={14} className="text-gray" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SchemesGrid({ schemes, loading, onEdit, onToggle, onDelete }) {
  if (loading) return <div className="panel" style={{ padding: '24px', textAlign: 'center', color: 'var(--g3)' }}>Loading…</div>;
  if (schemes.length === 0) {
    return (
      <div className="panel" style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>
        No loan schemes yet. Click <strong>Add Loan Scheme</strong> to create one.
      </div>
    );
  }
  return (
    <div className="services-grid">
      {schemes.map((s) => (
        <div key={s.id} className="service-card" style={{ cursor: 'default' }}>
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <span style={{ fontSize: '20px' }}>💰</span>
              <div>
                <div className="font-heading font-bold text-navy">{s.name}</div>
                <div className="text-xs text-gray">{s.slug}</div>
              </div>
            </div>
            <button
              className={`p-1 ${s.isActive ? 'text-green-600' : 'text-gray-400'}`}
              onClick={() => onToggle(s)}
              title={s.isActive ? 'Click to deactivate' : 'Click to activate'}
            >
              {s.isActive ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
            </button>
          </div>
          <div className="text-sm text-gray mb-2">{s.description || 'No description'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '11px', color: 'var(--g3)', marginBottom: '8px' }}>
            <div><span style={{ color: 'var(--g4)' }}>GL Code:</span> <strong style={{ color: 'var(--navy)' }}>{s.glCode ?? '—'}</strong></div>
            <div><span style={{ color: 'var(--g4)' }}>Rate:</span> <strong style={{ color: 'var(--navy)' }}>{s.interestRate ? `${s.interestRate}%` : '—'}</strong></div>
            <div><span style={{ color: 'var(--g4)' }}>Tenure:</span> <strong style={{ color: 'var(--navy)' }}>{s.tenureMonths ? `${s.tenureMonths} mo` : '—'}</strong></div>
            <div><span style={{ color: 'var(--g4)' }}>Min:</span> <strong style={{ color: 'var(--navy)' }}>{fmtINR(s.minAmount)}</strong></div>
            <div><span style={{ color: 'var(--g4)' }}>Max:</span> <strong style={{ color: 'var(--navy)' }}>{fmtINR(s.maxAmount)}</strong></div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="text-xs text-gray">Loans on scheme: <strong style={{ color: 'var(--navy)' }}>{s.loanCount || 0}</strong></div>
            <div className="flex gap-2">
              <button className="agent-view-link" onClick={() => onEdit(s)}><Edit2 size={12} /></button>
              <button className="agent-view-link text-red" onClick={() => onDelete(s)} title="Delete scheme"><Trash2 size={12} /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
